import React from "react";
import { fieldValidation, IncidentType, rng } from "../../../../amorg-common";
import { portalClientApi } from '../../../../api/portalClientApi';
import { Card } from "../../../../designSystem/Card";
import { FileUploadSlotPanel } from "../../../../designSystem/FileUploadSlotPanel";
import { LoadingPageOverlay } from "../../../../designSystem/LoadingPageOverlay";
import { MessageCard } from "../../../../designSystem/Message";
import { Button } from "../../../../designSystem/button/Button";
import { CollapsiblePanel } from "../../../../designSystem/collapsible/CollapsiblePanel";
import { colors } from "../../../../designSystem/colors/color";
import { Flex } from "../../../../designSystem/compositeComponents/FlexHelpers";
import { TextField } from "../../../../designSystem/formField/text/TextField";
import { TextAreaField } from "../../../../designSystem/formField/textArea/TextAreaField";
import { Icon } from "../../../../designSystem/icons/Icons";
import { OnOff } from "../../../../designSystem/inputs/OnOff";
import { P } from "../../../../designSystem/typography/Body";


export type IncidentInputData = {
    title: string;
    description: string;
    solutionDescription: string;
    anonymousSelected: boolean,
    email: string;
    name: string;
    notificationsSelected: boolean;
    fileUploadSlotId:string
}

export function GeneralCreateIncidentPanel(props:{
    type:IncidentType
}):JSX.Element
{
    const [inputData, setInputData] = React.useState<IncidentInputData>({
        title: '',
        description: '',
        solutionDescription: '',
        anonymousSelected: false,
        email: '',
        name: '',
        notificationsSelected: false,
        fileUploadSlotId:''
    });
    const [submitResult, setSubmitResult] = React.useState<'pending' | 'input-error' | 'server-error' | 'success'>('pending');
    const [loading, setLoading] = React.useState(false);

    async function handleCreateIncident(): Promise<void> {
        if(!valid) {
            setSubmitResult('input-error');
            return;
        }
        
        setLoading(true);

        const id = rng.getRandomId('incident_');

        const name = inputData.anonymousSelected ? '' : inputData.name;
        const email = (inputData.anonymousSelected && !inputData.notificationsSelected) ? '' : inputData.email;

        const createResult = await portalClientApi.createIncident(
            id,
            props.type,
            inputData.title,
            inputData.description,
            inputData.solutionDescription || '',
            inputData.anonymousSelected,
            email,
            name,
            inputData.notificationsSelected,
            inputData.fileUploadSlotId
        );

        if (createResult.isSuccess) {
            setSubmitResult('success');
        }
        else {
            setSubmitResult('server-error');
        }
        setLoading(false);
    }

    const fieldErrors = { title:'', description:'', solutionDescription:'', email:'', name:'' };
    if(!fieldValidation.incident.title(inputData.title)) 
        fieldErrors.title = `Skal udfyldes. Maks. 100 tegn (${inputData.title.length}).`;
    
    if(!fieldValidation.incident.description(inputData.description)) 
        fieldErrors.description = `Skal udfyldes. Maks. 20000 tegn (${inputData.description.length}).`;
    
    if(props.type === 'almost-accident' && !fieldValidation.incident.solutionDescription(inputData.solutionDescription || '')) 
        fieldErrors.solutionDescription = `Skal udfyldes. Maks. 20000 tegn (${inputData.solutionDescription?.length || 0}).`;
    
    if(!fieldValidation.incident.email(inputData.email, inputData.anonymousSelected, inputData.notificationsSelected))
        fieldErrors.email = `Email skal udfyldes med en gyldig e-mailaddresse. Maks. 100 tegn (${inputData.email.length}).`;

    if(!fieldValidation.incident.name(inputData.name, inputData.anonymousSelected))
        fieldErrors.name = `Navn skal udfyldes. Maks. 100 tegn (${inputData.name.length}).`;

    const valid = !(fieldErrors.title || fieldErrors.description || fieldErrors.solutionDescription || fieldErrors.email || fieldErrors.name);
    
    return (
        <Flex flexDirection='column' gap={16}>
            <LoadingPageOverlay loading={loading} />

            <Flex flexDirection="column" gap={16}>

                {submitResult === 'input-error' &&
                    <MessageCard variant="error">                        
                        <P paddingTop>Der er fejl på siden - ret fejlene og prøv igen</P>
                    </MessageCard>
                }


                {submitResult === 'server-error' &&
                    <MessageCard variant="error">
                        <P paddingTop>Der skete en fejl ved oprettelsen af indberetningen. Prøv igen.</P>
                        <P>Hvis fejlen fortsætter, er du velkommen til at kontakte AMORG support.</P>
                    </MessageCard>
                }

                {submitResult === 'success' &&
                    <MessageCard variant="success">
                        <P paddingTop>Indberetningen er oprettet</P>
                        <P>Hvis du har delt din e-mail addresse med os, vil vi sende dig notifikationer hvis din arbejdsmiljøorganisation reagerer på din indberetning.</P>
                        <P>Tak fordi du bidrager til et bedre arbejdsmiljø!</P>
                    </MessageCard>
                }

                <Flex flexDirection="column" gap={4}>
                    
                    <TextField 
                        value={inputData.title} 
                        label={"Emne"}
                        placeholder={"Emne"}
                        errorMessage={submitResult !== 'pending' ? fieldErrors.title : ''}
                        disabled={submitResult === 'success'}
                        onChange={ (value) => setInputData({ ...inputData, title: value })} 
                    />
                </Flex>
                <Flex flexDirection="column" gap={4}>
                    <TextAreaField 
                        label="Beskrivelse"
                        value={inputData.description} 
                        placeholder="Skriv en beskrivelse af hændelsen"
                        minRows={6}
                        errorMessage={submitResult !== 'pending' ? fieldErrors.description : ''}
                        disabled={submitResult === 'success'}
                        onChange={ (value) => setInputData({ ...inputData, description: value })} 
                    />
                </Flex>

                {props.type === 'almost-accident' &&
                    <Flex flexDirection="column" gap={4}>
                        <TextAreaField 
                            label="Løsningforslag"
                            value={inputData.solutionDescription || ''}
                            placeholder="Skriv evt. løsningforslag" 
                            minRows={6}
                            errorMessage={submitResult !== 'pending' ? fieldErrors.solutionDescription : ''}
                            disabled={submitResult === 'success'}
                            onChange={ (value) => setInputData({ ...inputData, solutionDescription: value })} 
                        />
                    </Flex>
                }

                {/* Only show file upload slot if incident is not yet successfully submitted, as files will be changed to reference the incident after creation */}
                {submitResult !== 'success' &&
                    <CollapsiblePanel 
                        defaultOpen 
                        closedOrDefaultHeader={
                            <Flex gap={8}><Icon name='file_image' color={colors.icon.active} />Vedhæft billeder (valgfrit)</Flex>
                        }>

                        <FileUploadSlotPanel onUploadslotCreated={(id) => 
                            setInputData({ ...inputData, fileUploadSlotId: id })} />

                    </CollapsiblePanel>
                }
                
                <SettingsSection inputData={inputData} 
                    errors={submitResult !== 'pending' ? fieldErrors : { email: '', name: '' }} 
                    disabled={submitResult === 'success'}
                    onChange={(data) => setInputData(data)} />

                <Flex flexDirection="row" justifyContent="flex-end">
                    <Button 
                        variant="filled" 
                        onClick={ () => handleCreateIncident()}
                        text="Opret indberetning" 
                        disabled={submitResult === 'success'} />
                        
                </Flex>
            </Flex>
        </Flex>
    );

}



function SettingsSection(props:{
    inputData: IncidentInputData,
    errors:{ email:string, name:string },
    disabled: boolean,
    onChange: (data:IncidentInputData) => void
}):JSX.Element
{
    return (
        <Flex flexDirection="column" gap={32}>

            <Flex flexDirection="column" gap={16}>

                <Flex flexDirection="row" gap={16} alignItems="center" justifyContent="flex-end">

                    <span>Jeg ønsker at være anonym</span>
                    <OnOff active={props.inputData.anonymousSelected}
                        onClick={ (value) => props.onChange({ ...props.inputData, anonymousSelected: value })}
                        disabled={props.disabled} />

                </Flex>

                
                <Flex flexDirection="row" gap={16} alignItems="center" justifyContent="flex-end">

                    <span>Min arbejdsmiljøorganisation må gerne kontakte mig</span> 
                    <OnOff active={props.inputData.notificationsSelected}
                        onClick={ (value) => props.onChange({ ...props.inputData, notificationsSelected: value })}
                        disabled={props.disabled} />

                </Flex>

                <Card variant="lightGray">
                    <Flex flexDirection="column" gap={24}>

                        {props.inputData.anonymousSelected && props.inputData.notificationsSelected &&
                            <>
                                <Flex flexDirection="row" gap={8} alignItems="flex-start">
                                    <Icon name="domino_mask" />
                                    <div>Du skal ikke indtaste dit navn</div>
                                </Flex>

                                <Flex flexDirection="row" gap={8} alignItems="flex-start">
                                    <Icon name="interface_glyph_email" />
                                    <div>Din arbejdsgiver kan ikke se din e-mail addresse, men kan kontakte dig anonymt gennem AMORGs platform</div>
                                </Flex>

                                <Flex flexDirection="row" gap={8} alignItems="flex-start">
                                    <Icon name="comment" />
                                    <div>Du vil modtage en notifikation hvis din arbejdsgiver vælger at svare på din indberetning, og kan svare anonymt</div>
                                </Flex>
                            </>
                        }

                        {props.inputData.anonymousSelected && !props.inputData.notificationsSelected &&
                            <>
                                <Flex flexDirection="row" gap={8} alignItems="flex-start">
                                    <Icon name="domino_mask" />
                                    <div>Du skal ikke indtaste dit navn</div>
                                </Flex>

                                <Flex flexDirection="row" gap={8} alignItems="flex-start">
                                    <Icon name="mail_off" />
                                    <div>Du skal ikke indtaste din e-mail addresse</div>
                                </Flex>

                                <Flex flexDirection="row" gap={8} alignItems="flex-start">
                                    <Icon name="comments_disabled" />
                                    <div>Din arbejdsgiver kan ikke kontakte dig</div>
                                </Flex>
                            </>
                        }

                        {!props.inputData.anonymousSelected && props.inputData.notificationsSelected &&
                            <>
                                <Flex flexDirection="row" gap={8} alignItems="flex-start">
                                    <Icon name="interface_user_user" />
                                    <div>Din arbejdsgiver kan se dit navn</div>
                                </Flex>

                                <Flex flexDirection="row" gap={8} alignItems="flex-start">
                                    <Icon name="interface_actions_mail" />
                                    <div>Din arbejdsgiver kan se din e-mail addresse</div>
                                </Flex>

                                <Flex flexDirection="row" gap={8} alignItems="flex-start">
                                    <Icon name="comment" />
                                    <div>Du vil modtage en notifikation hvis din arbejdsgiver vælger at svare på din indberetning gennem AMORGs platform</div>
                                </Flex>
                            </>
                        }

                        {!props.inputData.anonymousSelected && !props.inputData.notificationsSelected &&
                            <>
                                <Flex flexDirection="row" gap={8} alignItems="flex-start">
                                    <Icon name="interface_user_user" />
                                    <div>Din arbejdsgiver kan se dit navn</div>
                                </Flex>

                                <Flex flexDirection="row" gap={8} alignItems="flex-start">
                                    <Icon name="interface_actions_mail" />
                                    <div>Din arbejdsgiver kan se din e-mail addresse</div>
                                </Flex>

                                <Flex flexDirection="row" gap={8} alignItems="flex-start">
                                    <Icon name="comments_disabled" />
                                    <div>Din arbejdsgiver kan ikke kontakte dig gennem AMORGs platform</div>
                                </Flex>
                            </>
                        }
                    </Flex>
                </Card>
            </Flex>

            
            <Flex flexDirection="row" gap={16} alignItems="flex-start">
                
                <div style={{width:'50%'}}>
                    {!props.inputData.anonymousSelected && (
                        <TextField 
                            label={"Navn"} 
                            value={props.inputData.name}
                            errorMessage={props.errors.name}
                            placeholder='Indtast dit navn'
                            disabled={props.disabled}
                            onChange={(value) => props.onChange({...props.inputData, name:value})} />
                    )}
                </div>

                <div style={{width:'50%'}}>
                    {(!props.inputData.anonymousSelected || props.inputData.notificationsSelected) &&
                        <TextField 
                            label={"Email"} 
                            value={props.inputData.email} 
                            errorMessage={props.errors.email}
                            placeholder='Indtast din emailadresse'
                            disabled={props.disabled}
                            onChange={(value) => props.onChange({...props.inputData, email:value})} />
                    }
                </div>
            </Flex>
        </Flex>
    );
}